// src/components/ReplayManagement/ReplaySettings.js
import React, { Component } from 'react';
import {
	Modal,
	Form,
	Input,
	Select,
	Switch,
	Slider,
	InputNumber,
	Button,
	Space,
	Divider,
	Typography,
	Card,
	Row,
	Col,
	Tabs,
	Alert,
	Tooltip,
	Radio,
	Checkbox,
	Upload,
	message,
	Progress,
	Tag,
	List,
	Popconfirm
} from 'antd';
import {
	SettingOutlined,
	SaveOutlined,
	ReloadOutlined,
	ExportOutlined,
	ImportOutlined,
	DeleteOutlined,
	InfoCircleOutlined,
	CloudUploadOutlined,
	FolderOutlined,
	PlayCircleOutlined,
	VideoCameraOutlined,
	SoundOutlined,
	DatabaseOutlined,
	SecurityScanOutlined,
	GlobalOutlined,
	BugOutlined,
	QuestionCircleOutlined,
	CheckCircleOutlined,
	WarningOutlined,
	CloseOutlined
} from '@ant-design/icons';
import './ReplaySettings.scss';

const { Title, Text, Paragraph } = Typography;
const { TabPane } = Tabs;
const { Option } = Select;
const { TextArea } = Input;

class ReplaySettings extends Component {
	constructor(props) {
		super(props);
		this.state = {
			// Settings data
			settings: {
				// Recording settings
				recording: {
					quality: 'high',
					fps: 60,
					bitrate: 8000,
					format: 'mp4',
					autoRecord: true,
					bufferSize: 30,
					audioEnabled: true,
					audioQuality: 'high',
					micEnabled: false,
					micVolume: 50
				},
				// Storage settings
				storage: {
					localPath: '',
					cloudEnabled: false,
					cloudProvider: 'aws',
					maxLocalStorage: 100,
					autoCleanup: true,
					cleanupDays: 30,
					compressionEnabled: true,
					compressionLevel: 'medium'
				},
				// Playback settings
				playback: {
					defaultSpeed: 1,
					autoPlay: false,
					showTimestamps: true,
					showChat: true,
					chatOpacity: 80,
					skipIntro: false,
					loopEnabled: false,
					preloadNext: true
				},
				// Privacy settings
				privacy: {
					publicReplays: false,
					allowDownloads: true,
					allowSharing: true,
					hideViewerCount: false,
					anonymizeChat: false,
					blurSensitiveInfo: true
				},
				// Performance settings
				performance: {
					hardwareAcceleration: true,
					maxConcurrentRecordings: 3,
					backgroundProcessing: true,
					lowLatencyMode: false,
					memoryLimit: 4096,
					diskCacheSize: 1024
				},
				// Notification settings
				notifications: {
					recordingStart: true,
					recordingEnd: true,
					processingComplete: true,
					storageWarning: true,
					errorAlerts: true,
					emailNotifications: false,
					pushNotifications: true
				}
			},
			
			// UI state
			activeTab: 'recording',
			loading: false,
			saving: false,
			importing: false,
			exporting: false,
			hasChanges: false,
			
			// Validation
			errors: {},
			
			// Advanced settings
			showAdvanced: false,
			
			// Import/Export
			importData: null,
			exportProgress: 0
		};

		this.formRef = React.createRef();
	}

	componentDidMount() {
		this.loadSettings();
	}

	componentDidUpdate(prevProps) {
		if (prevProps.visible !== this.props.visible && this.props.visible) {
			this.loadSettings();
		}
	}

	loadSettings = async () => {
		this.setState({ loading: true });
		
		try {
			// Simulate API call
			await new Promise(resolve => setTimeout(resolve, 1000));
			
			// Load settings from localStorage or API
			const savedSettings = localStorage.getItem('replaySettings');
			if (savedSettings) {
				const parsedSettings = JSON.parse(savedSettings);
				this.setState({
					settings: { ...this.state.settings, ...parsedSettings },
					loading: false,
					hasChanges: false
				});
			} else {
				this.setState({ loading: false });
			}
		} catch (error) {
			console.error('Failed to load settings:', error);
			message.error('Failed to load settings');
			this.setState({ loading: false });
		}
	}

	saveSettings = async () => {
		this.setState({ saving: true });
		
		try {
			// Validate settings
			const validation = this.validateSettings();
			if (!validation.isValid) {
				this.setState({ errors: validation.errors, saving: false });
				message.error('Please fix validation errors');
				return;
			}

			// Simulate API call
			await new Promise(resolve => setTimeout(resolve, 1500));
			
			// Save to localStorage (in real app, this would be an API call)
			localStorage.setItem('replaySettings', JSON.stringify(this.state.settings));
			
			this.setState({ 
				saving: false, 
				hasChanges: false,
				errors: {}
			});
			
			message.success('Settings saved successfully');
			
			// Call parent callback if provided
			if (this.props.onSettingsChange) {
				this.props.onSettingsChange(this.state.settings);
			}
		} catch (error) {
			console.error('Failed to save settings:', error);
			message.error('Failed to save settings');
			this.setState({ saving: false });
		}
	}

	resetSettings = () => {
		Modal.confirm({
			title: 'Reset Settings',
			content: 'Are you sure you want to reset all settings to default values? This action cannot be undone.',
			okText: 'Reset',
			okType: 'danger',
			cancelText: 'Cancel',
			onOk: () => {
				// Reset to default settings
				const defaultSettings = {
					recording: {
						quality: 'high',
						fps: 60,
						bitrate: 8000,
						format: 'mp4',
						autoRecord: true,
						bufferSize: 30,
						audioEnabled: true,
						audioQuality: 'high',
						micEnabled: false,
						micVolume: 50
					},
					storage: {
						localPath: '',
						cloudEnabled: false,
						cloudProvider: 'aws',
						maxLocalStorage: 100,
						autoCleanup: true,
						cleanupDays: 30,
						compressionEnabled: true,
						compressionLevel: 'medium'
					},
					playback: {
						defaultSpeed: 1,
						autoPlay: false,
						showTimestamps: true,
						showChat: true,
						chatOpacity: 80,
						skipIntro: false,
						loopEnabled: false,
						preloadNext: true
					},
					privacy: {
						publicReplays: false,
						allowDownloads: true,
						allowSharing: true,
						hideViewerCount: false,
						anonymizeChat: false,
						blurSensitiveInfo: true
					},
					performance: {
						hardwareAcceleration: true,
						maxConcurrentRecordings: 3,
						backgroundProcessing: true,
						lowLatencyMode: false,
						memoryLimit: 4096,
						diskCacheSize: 1024
					},
					notifications: {
						recordingStart: true,
						recordingEnd: true,
						processingComplete: true,
						storageWarning: true,
						errorAlerts: true,
						emailNotifications: false,
						pushNotifications: true
					}
				};

				this.setState({
					settings: defaultSettings,
					hasChanges: true,
					errors: {}
				});

				message.success('Settings reset to default values');
			}
		});
	}

	validateSettings = () => {
		const { settings } = this.state;
		const errors = {};
		let isValid = true;

		// Validate recording settings
		if (settings.recording.fps < 15 || settings.recording.fps > 120) {
			errors.fps = 'FPS must be between 15 and 120';
			isValid = false;
		}

		if (settings.recording.bitrate < 1000 || settings.recording.bitrate > 50000) {
			errors.bitrate = 'Bitrate must be between 1000 and 50000 kbps';
			isValid = false;
		}

		// Validate storage settings
		if (settings.storage.maxLocalStorage < 1 || settings.storage.maxLocalStorage > 1000) {
			errors.maxLocalStorage = 'Max storage must be between 1 and 1000 GB';
			isValid = false;
		}

		if (settings.storage.cleanupDays < 1 || settings.storage.cleanupDays > 365) {
			errors.cleanupDays = 'Cleanup days must be between 1 and 365';
			isValid = false;
		}

		// Validate performance settings
		if (settings.performance.maxConcurrentRecordings < 1 || settings.performance.maxConcurrentRecordings > 10) {
			errors.maxConcurrentRecordings = 'Max concurrent recordings must be between 1 and 10';
			isValid = false;
		}

		return { isValid, errors };
	}

	handleSettingChange = (category, key, value) => {
		this.setState(prevState => ({
			settings: {
				...prevState.settings,
				[category]: {
					...prevState.settings[category],
					[key]: value
				}
			},
			hasChanges: true,
			errors: {
				...prevState.errors,
				[key]: undefined
			}
		}));
	}

	handleExportSettings = async () => {
		this.setState({ exporting: true, exportProgress: 0 });
		
		try {
			// Simulate export progress
			for (let i = 0; i <= 100; i += 10) {
				await new Promise(resolve => setTimeout(resolve, 100));
				this.setState({ exportProgress: i });
			}

			const exportData = {
				version: '1.0.0',
				timestamp: new Date().toISOString(),
				settings: this.state.settings
			};

			const dataStr = JSON.stringify(exportData, null, 2);
			const dataBlob = new Blob([dataStr], { type: 'application/json' });
			const url = URL.createObjectURL(dataBlob);
			
			const link = document.createElement('a');
			link.href = url;
			link.download = `replay-settings-${new Date().toISOString().split('T')[0]}.json`;
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);
			
			URL.revokeObjectURL(url);
			
			this.setState({ exporting: false, exportProgress: 0 });
			message.success('Settings exported successfully');
		} catch (error) {
			console.error('Export failed:', error);
			message.error('Failed to export settings');
			this.setState({ exporting: false, exportProgress: 0 });
		}
	}

	handleImportSettings = (file) => {
		this.setState({ importing: true });
		
		const reader = new FileReader();
		reader.onload = (e) => {
			try {
				const importedData = JSON.parse(e.target.result);
				
				if (!importedData.settings) {
					throw new Error('Invalid settings file format');
				}

				this.setState({
					settings: { ...this.state.settings, ...importedData.settings },
					hasChanges: true,
					importing: false
				});

				message.success('Settings imported successfully');
			} catch (error) {
				console.error('Import failed:', error);
				message.error('Failed to import settings: Invalid file format');
				this.setState({ importing: false });
			}
		};

		reader.onerror = () => {
			message.error('Failed to read file');
			this.setState({ importing: false });
		};

		reader.readAsText(file);
		return false; // Prevent upload
	}

	renderRecordingSettings = () => {
		const { settings, errors } = this.state;
		const { recording } = settings;

		return (
			<div className="settings-section">
				<Title level={4}>
					<VideoCameraOutlined /> Recording Settings
				</Title>
				
				<Row gutter={[16, 16]}>
					<Col xs={24} md={12}>
						<Form.Item 
							label="Video Quality"
							help="Higher quality requires more storage space"
						>
							<Select
								value={recording.quality}
								onChange={(value) => this.handleSettingChange('recording', 'quality', value)}
							>
								<Option value="low">Low (720p)</Option>
								<Option value="medium">Medium (1080p)</Option>
								<Option value="high">High (1440p)</Option>
								<Option value="ultra">Ultra (4K)</Option>
							</Select>
						</Form.Item>
					</Col>
					
					<Col xs={24} md={12}>
						<Form.Item 
							label="Frame Rate (FPS)"
							validateStatus={errors.fps ? 'error' : ''}
							help={errors.fps || 'Frames per second for recording'}
						>
							<InputNumber
								min={15}
								max={120}
								value={recording.fps}
								onChange={(value) => this.handleSettingChange('recording', 'fps', value)}
								style={{ width: '100%' }}
							/>
						</Form.Item>
					</Col>
					
					<Col xs={24} md={12}>
						<Form.Item 
							label="Bitrate (kbps)"
							validateStatus={errors.bitrate ? 'error' : ''}
							help={errors.bitrate || 'Higher bitrate means better quality but larger files'}
						>
							<Slider
								min={1000}
								max={20000}
								step={500}
								value={recording.bitrate}
								onChange={(value) => this.handleSettingChange('recording', 'bitrate', value)}
								marks={{
									1000: '1M',
									5000: '5M',
									10000: '10M',
									15000: '15M',
									20000: '20M'
								}}
							/>
						</Form.Item>
					</Col>
					
					<Col xs={24} md={12}>
						<Form.Item label="Output Format">
							<Radio.Group
								value={recording.format}
								onChange={(e) => this.handleSettingChange('recording', 'format', e.target.value)}
							>
								<Radio value="mp4">MP4</Radio>
								<Radio value="mkv">MKV</Radio>
								<Radio value="avi">AVI</Radio>
							</Radio.Group>
						</Form.Item>
					</Col>
					
					<Col xs={24}>
						<Divider />
						<Title level={5}>
							<SoundOutlined /> Audio Settings
						</Title>
					</Col>
					
					<Col xs={24} md={12}>
						<Form.Item>
							<Space direction="vertical" style={{ width: '100%' }}>
								<Switch
									checked={recording.audioEnabled}
									onChange={(checked) => this.handleSettingChange('recording', 'audioEnabled', checked)}
									checkedChildren="Audio On"
									unCheckedChildren="Audio Off"
								/>
								<Text type="secondary">Record game audio</Text>
							</Space>
						</Form.Item>
					</Col>
					
					<Col xs={24} md={12}>
						<Form.Item>
							<Space direction="vertical" style={{ width: '100%' }}>
								<Switch
									checked={recording.micEnabled}
									onChange={(checked) => this.handleSettingChange('recording', 'micEnabled', checked)}
									checkedChildren="Mic On"
									unCheckedChildren="Mic Off"
								/>
								<Text type="secondary">Record microphone</Text>
							</Space>
						</Form.Item>
					</Col>
					
					{recording.audioEnabled && (
						<Col xs={24} md={12}>
							<Form.Item label="Audio Quality">
								<Select
									value={recording.audioQuality}
									onChange={(value) => this.handleSettingChange('recording', 'audioQuality', value)}
								>
									<Option value="low">Low (128kbps)</Option>
									<Option value="medium">Medium (192kbps)</Option>
									<Option value="high">High (320kbps)</Option>
								</Select>
							</Form.Item>
						</Col>
					)}
					
					{recording.micEnabled && (
						<Col xs={24} md={12}>
							<Form.Item label="Microphone Volume">
								<Slider
									min={0}
									max={100}
									value={recording.micVolume}
									onChange={(value) => this.handleSettingChange('recording', 'micVolume', value)}
									marks={{ 0: '0%', 50: '50%', 100: '100%' }}
								/>
							</Form.Item>
						</Col>
					)}
					
					<Col xs={24}>
						<Form.Item>
							<Space direction="vertical" style={{ width: '100%' }}>
								<Switch
									checked={recording.autoRecord}
									onChange={(checked) => this.handleSettingChange('recording', 'autoRecord', checked)}
									checkedChildren="Auto Record On"
									unCheckedChildren="Auto Record Off"
								/>
								<Text type="secondary">Automatically start recording when streaming begins</Text>
							</Space>
						</Form.Item>
					</Col>
				</Row>
			</div>
		);
	}

	renderStorageSettings = () => {
		const { settings, errors } = this.state;
		const { storage } = settings;

		return (
			<div className="settings-section">
				<Title level={4}>
					<DatabaseOutlined /> Storage Settings
				</Title>
				
				<Row gutter={[16, 16]}>
					<Col xs={24}>
						<Form.Item 
							label="Local Storage Path"
							help="Directory where replays will be saved locally"
						>
							<Input.Group compact>
								<Input
									style={{ width: 'calc(100% - 80px)' }}
									value={storage.localPath}
									onChange={(e) => this.handleSettingChange('storage', 'localPath', e.target.value)}
									placeholder="Select folder for replay storage"
								/>
								<Button icon={<FolderOutlined />}>Browse</Button>
							</Input.Group>
						</Form.Item>
					</Col>
					
					<Col xs={24} md={12}>
						<Form.Item 
							label="Max Local Storage (GB)"
							validateStatus={errors.maxLocalStorage ? 'error' : ''}
							help={errors.maxLocalStorage || 'Maximum disk space to use for replays'}
						>
							<InputNumber
								min={1}
								max={1000}
								value={storage.maxLocalStorage}
								onChange={(value) => this.handleSettingChange('storage', 'maxLocalStorage', value)}
								style={{ width: '100%' }}
								formatter={value => `${value} GB`}
								parser={value => value.replace(' GB', '')}
							/>
						</Form.Item>
					</Col>
					
					<Col xs={24} md={12}>
						<Form.Item>
							<Space direction="vertical" style={{ width: '100%' }}>
								<Switch
									checked={storage.autoCleanup}
									onChange={(checked) => this.handleSettingChange('storage', 'autoCleanup', checked)}
									checkedChildren="Auto Cleanup On"
									unCheckedChildren="Auto Cleanup Off"
								/>
								<Text type="secondary">Automatically delete old replays</Text>
							</Space>
						</Form.Item>
					</Col>
					
					{storage.autoCleanup && (
						<Col xs={24} md={12}>
							<Form.Item 
								label="Cleanup After (Days)"
								validateStatus={errors.cleanupDays ? 'error' : ''}
								help={errors.cleanupDays || 'Delete replays older than this many days'}
							>
								<InputNumber
									min={1}
									max={365}
									value={storage.cleanupDays}
									onChange={(value) => this.handleSettingChange('storage', 'cleanupDays', value)}
									style={{ width: '100%' }}
								/>
							</Form.Item>
						</Col>
					)}
					
					<Col xs={24}>
						<Divider />
						<Title level={5}>
							<CloudUploadOutlined /> Cloud Storage
						</Title>
					</Col>
					
					<Col xs={24} md={12}>
						<Form.Item>
							<Space direction="vertical" style={{ width: '100%' }}>
								<Switch
									checked={storage.cloudEnabled}
									onChange={(checked) => this.handleSettingChange('storage', 'cloudEnabled', checked)}
									checkedChildren="Cloud On"
									unCheckedChildren="Cloud Off"
								/>
								<Text type="secondary">Enable cloud backup for replays</Text>
							</Space>
						</Form.Item>
					</Col>
					
					{storage.cloudEnabled && (
						<Col xs={24} md={12}>
							<Form.Item label="Cloud Provider">
								<Select
									value={storage.cloudProvider}
									onChange={(value) => this.handleSettingChange('storage', 'cloudProvider', value)}
								>
									<Option value="aws">Amazon S3</Option>
									<Option value="gcp">Google Cloud</Option>
									<Option value="azure">Azure Blob</Option>
									<Option value="dropbox">Dropbox</Option>
								</Select>
							</Form.Item>
						</Col>
					)}
					
					<Col xs={24}>
						<Divider />
						<Title level={5}>Compression</Title>
					</Col>
					
					<Col xs={24} md={12}>
						<Form.Item>
							<Space direction="vertical" style={{ width: '100%' }}>
								<Switch
									checked={storage.compressionEnabled}
									onChange={(checked) => this.handleSettingChange('storage', 'compressionEnabled', checked)}
									checkedChildren="Compression On"
									unCheckedChildren="Compression Off"
								/>
								<Text type="secondary">Compress replays to save space</Text>
							</Space>
						</Form.Item>
					</Col>
					
					{storage.compressionEnabled && (
						<Col xs={24} md={12}>
							<Form.Item label="Compression Level">
								<Select
									value={storage.compressionLevel}
									onChange={(value) => this.handleSettingChange('storage', 'compressionLevel', value)}
								>
									<Option value="low">Low (Fast, Larger Files)</Option>
									<Option value="medium">Medium (Balanced)</Option>
									<Option value="high">High (Slow, Smaller Files)</Option>
								</Select>
							</Form.Item>
						</Col>
					)}
				</Row>
			</div>
		);
	}

	renderPlaybackSettings = () => {
		const { settings } = this.state;
		const { playback } = settings;

		return (
			<div className="settings-section">
				<Title level={4}>
					<PlayCircleOutlined /> Playback Settings
				</Title>
				
				<Row gutter={[16, 16]}>
					<Col xs={24} md={12}>
						<Form.Item label="Default Playback Speed">
							<Select
								value={playback.defaultSpeed}
								onChange={(value) => this.handleSettingChange('playback', 'defaultSpeed', value)}
							>
								<Option value={0.25}>0.25x</Option>
								<Option value={0.5}>0.5x</Option>
								<Option value={0.75}>0.75x</Option>
								<Option value={1}>1x (Normal)</Option>
								<Option value={1.25}>1.25x</Option>
								<Option value={1.5}>1.5x</Option>
								<Option value={2}>2x</Option>
							</Select>
						</Form.Item>
					</Col>
					
					<Col xs={24} md={12}>
						<Form.Item label="Chat Opacity">
							<Slider
								min={0}
								max={100}
								value={playback.chatOpacity}
								onChange={(value) => this.handleSettingChange('playback', 'chatOpacity', value)}
								marks={{ 0: '0%', 50: '50%', 100: '100%' }}
								disabled={!playback.showChat}
							/>
						</Form.Item>
					</Col>
					
					<Col xs={24}>
						<Space direction="vertical" size="middle" style={{ width: '100%' }}>
							<div>
								<Switch
									checked={playback.autoPlay}
									onChange={(checked) => this.handleSettingChange('playback', 'autoPlay', checked)}
									checkedChildren="Auto Play On"
									unCheckedChildren="Auto Play Off"
								/>
								<Text type="secondary" style={{ marginLeft: 8 }}>
									Automatically start playing when replay loads
								</Text>
							</div>
							
							<div>
								<Switch
									checked={playback.showTimestamps}
									onChange={(checked) => this.handleSettingChange('playback', 'showTimestamps', checked)}
									checkedChildren="Timestamps On"
									unCheckedChildren="Timestamps Off"
								/>
								<Text type="secondary" style={{ marginLeft: 8 }}>
									Show timestamps during playback
								</Text>
							</div>
							
							<div>
								<Switch
									checked={playback.showChat}
									onChange={(checked) => this.handleSettingChange('playback', 'showChat', checked)}
									checkedChildren="Chat On"
									unCheckedChildren="Chat Off"
								/>
								<Text type="secondary" style={{ marginLeft: 8 }}>
									Display chat messages during playback
								</Text>
							</div>
							
							<div>
								<Switch
									checked={playback.skipIntro}
									onChange={(checked) => this.handleSettingChange('playback', 'skipIntro', checked)}
									checkedChildren="Skip Intro On"
									unCheckedChildren="Skip Intro Off"
								/>
								<Text type="secondary" style={{ marginLeft: 8 }}>
									Automatically skip intro sequences
								</Text>
							</div>
							
							<div>
								<Switch
									checked={playback.loopEnabled}
									onChange={(checked) => this.handleSettingChange('playback', 'loopEnabled', checked)}
									checkedChildren="Loop On"
									unCheckedChildren="Loop Off"
								/>
								<Text type="secondary" style={{ marginLeft: 8 }}>
									Loop replay when it reaches the end
								</Text>
							</div>
							
							<div>
								<Switch
									checked={playback.preloadNext}
									onChange={(checked) => this.handleSettingChange('playback', 'preloadNext', checked)}
									checkedChildren="Preload On"
									unCheckedChildren="Preload Off"
								/>
								<Text type="secondary" style={{ marginLeft: 8 }}>
									Preload next replay for faster switching
								</Text>
							</div>
						</Space>
					</Col>
				</Row>
			</div>
		);
	}

	renderPrivacySettings = () => {
		const { settings } = this.state;
		const { privacy } = settings;

		return (
			<div className="settings-section">
				<Title level={4}>
					<SecurityScanOutlined /> Privacy & Security
				</Title>
				
				<Alert
					message="Privacy Notice"
					description="These settings control how your replays are shared and what information is visible to others."
					type="info"
					showIcon
					style={{ marginBottom: 16 }}
				/>
				
				<Row gutter={[16, 16]}>
					<Col xs={24}>
						<Space direction="vertical" size="middle" style={{ width: '100%' }}>
							<div>
								<Switch
									checked={privacy.publicReplays}
									onChange={(checked) => this.handleSettingChange('privacy', 'publicReplays', checked)}
									checkedChildren="Public On"
									unCheckedChildren="Public Off"
								/>
								<Text style={{ marginLeft: 8 }}>
									Make replays publicly discoverable
								</Text>
								<br />
								<Text type="secondary" style={{ marginLeft: 32, fontSize: '12px' }}>
									When enabled, others can find and view your replays
								</Text>
							</div>
							
							<div>
								<Switch
									checked={privacy.allowDownloads}
									onChange={(checked) => this.handleSettingChange('privacy', 'allowDownloads', checked)}
									checkedChildren="Downloads On"
									unCheckedChildren="Downloads Off"
								/>
								<Text style={{ marginLeft: 8 }}>
									Allow others to download your replays
								</Text>
								<br />
								<Text type="secondary" style={{ marginLeft: 32, fontSize: '12px' }}>
									Viewers can save replays to their device
								</Text>
							</div>
							
							<div>
								<Switch
									checked={privacy.allowSharing}
									onChange={(checked) => this.handleSettingChange('privacy', 'allowSharing', checked)}
									checkedChildren="Sharing On"
									unCheckedChildren="Sharing Off"
								/>
								<Text style={{ marginLeft: 8 }}>
									Allow sharing replay links
								</Text>
								<br />
								<Text type="secondary" style={{ marginLeft: 32, fontSize: '12px' }}>
									Enable social sharing features
								</Text>
							</div>
							
							<div>
								<Switch
									checked={privacy.hideViewerCount}
									onChange={(checked) => this.handleSettingChange('privacy', 'hideViewerCount', checked)}
									checkedChildren="Hidden"
									unCheckedChildren="Visible"
								/>
								<Text style={{ marginLeft: 8 }}>
									Hide viewer count
								</Text>
								<br />
								<Text type="secondary" style={{ marginLeft: 32, fontSize: '12px' }}>
									Don't show how many people watched the replay
								</Text>
							</div>
							
							<div>
								<Switch
									checked={privacy.anonymizeChat}
									onChange={(checked) => this.handleSettingChange('privacy', 'anonymizeChat', checked)}
									checkedChildren="Anonymous"
									unCheckedChildren="Show Names"
								/>
								<Text style={{ marginLeft: 8 }}>
									Anonymize chat messages
								</Text>
								<br/>
                                								<Text type="secondary" style={{ marginLeft: 32, fontSize: '12px' }}>
									Hide usernames in chat replay
								</Text>
							</div>
							
							<div>
								<Switch
									checked={privacy.blurSensitiveInfo}
									onChange={(checked) => this.handleSettingChange('privacy', 'blurSensitiveInfo', checked)}
									checkedChildren="Blur On"
									unCheckedChildren="Blur Off"
								/>
								<Text style={{ marginLeft: 8 }}>
									Blur sensitive information
								</Text>
								<br />
								<Text type="secondary" style={{ marginLeft: 32, fontSize: '12px' }}>
									Automatically blur personal info like emails, addresses
								</Text>
							</div>
						</Space>
					</Col>
				</Row>
			</div>
		);
	}

	renderPerformanceSettings = () => {
		const { settings, errors } = this.state;
		const { performance } = settings;

		return (
			<div className="settings-section">
				<Title level={4}>
					<BugOutlined /> Performance Settings
				</Title>
				
				<Alert
					message="Performance Impact"
					description="These settings affect system performance and resource usage. Adjust based on your hardware capabilities."
					type="warning"
					showIcon
					style={{ marginBottom: 16 }}
				/>
				
				<Row gutter={[16, 16]}>
					<Col xs={24} md={12}>
						<Form.Item 
							label="Max Concurrent Recordings"
							validateStatus={errors.maxConcurrentRecordings ? 'error' : ''}
							help={errors.maxConcurrentRecordings || 'Maximum number of simultaneous recordings'}
						>
							<InputNumber
								min={1}
								max={10}
								value={performance.maxConcurrentRecordings}
								onChange={(value) => this.handleSettingChange('performance', 'maxConcurrentRecordings', value)}
								style={{ width: '100%' }}
							/>
						</Form.Item>
					</Col>
					
					<Col xs={24} md={12}>
						<Form.Item 
							label="Memory Limit (MB)"
							help="Maximum RAM usage for replay processing"
						>
							<Slider
								min={1024}
								max={8192}
								step={512}
								value={performance.memoryLimit}
								onChange={(value) => this.handleSettingChange('performance', 'memoryLimit', value)}
								marks={{
									1024: '1GB',
									2048: '2GB',
									4096: '4GB',
									6144: '6GB',
									8192: '8GB'
								}}
							/>
						</Form.Item>
					</Col>
					
					<Col xs={24} md={12}>
						<Form.Item 
							label="Disk Cache Size (MB)"
							help="Temporary storage for processing"
						>
							<InputNumber
								min={256}
								max={4096}
								step={256}
								value={performance.diskCacheSize}
								onChange={(value) => this.handleSettingChange('performance', 'diskCacheSize', value)}
								style={{ width: '100%' }}
								formatter={value => `${value} MB`}
								parser={value => value.replace(' MB', '')}
							/>
						</Form.Item>
					</Col>
					
					<Col xs={24}>
						<Space direction="vertical" size="middle" style={{ width: '100%' }}>
							<div>
								<Switch
									checked={performance.hardwareAcceleration}
									onChange={(checked) => this.handleSettingChange('performance', 'hardwareAcceleration', checked)}
									checkedChildren="GPU On"
									unCheckedChildren="GPU Off"
								/>
								<Text style={{ marginLeft: 8 }}>
									Hardware acceleration
								</Text>
								<br />
								<Text type="secondary" style={{ marginLeft: 32, fontSize: '12px' }}>
									Use GPU for faster video processing (requires compatible hardware)
								</Text>
							</div>
							
							<div>
								<Switch
									checked={performance.backgroundProcessing}
									onChange={(checked) => this.handleSettingChange('performance', 'backgroundProcessing', checked)}
									checkedChildren="Background On"
									unCheckedChildren="Background Off"
								/>
								<Text style={{ marginLeft: 8 }}>
									Background processing
								</Text>
								<br />
								<Text type="secondary" style={{ marginLeft: 32, fontSize: '12px' }}>
									Continue processing when application is minimized
								</Text>
							</div>
							
							<div>
								<Switch
									checked={performance.lowLatencyMode}
									onChange={(checked) => this.handleSettingChange('performance', 'lowLatencyMode', checked)}
									checkedChildren="Low Latency On"
									unCheckedChildren="Low Latency Off"
								/>
								<Text style={{ marginLeft: 8 }}>
									Low latency mode
								</Text>
								<br />
								<Text type="secondary" style={{ marginLeft: 32, fontSize: '12px' }}>
									Reduce processing delay at the cost of higher resource usage
								</Text>
							</div>
						</Space>
					</Col>
				</Row>
			</div>
		);
	}

	renderNotificationSettings = () => {
		const { settings } = this.state;
		const { notifications } = settings;

		return (
			<div className="settings-section">
				<Title level={4}>
					<GlobalOutlined /> Notification Settings
				</Title>
				
				<Row gutter={[16, 16]}>
					<Col xs={24}>
						<Title level={5}>Recording Notifications</Title>
						<Space direction="vertical" size="middle" style={{ width: '100%' }}>
							<div>
								<Switch
									checked={notifications.recordingStart}
									onChange={(checked) => this.handleSettingChange('notifications', 'recordingStart', checked)}
									checkedChildren="On"
									unCheckedChildren="Off"
								/>
								<Text style={{ marginLeft: 8 }}>
									Recording started
								</Text>
							</div>
							
							<div>
								<Switch
									checked={notifications.recordingEnd}
									onChange={(checked) => this.handleSettingChange('notifications', 'recordingEnd', checked)}
									checkedChildren="On"
									unCheckedChildren="Off"
								/>
								<Text style={{ marginLeft: 8 }}>
									Recording finished
								</Text>
							</div>
							
							<div>
								<Switch
									checked={notifications.processingComplete}
									onChange={(checked) => this.handleSettingChange('notifications', 'processingComplete', checked)}
									checkedChildren="On"
									unCheckedChildren="Off"
								/>
								<Text style={{ marginLeft: 8 }}>
									Processing completed
								</Text>
							</div>
						</Space>
					</Col>
					
					<Col xs={24}>
						<Divider />
						<Title level={5}>System Notifications</Title>
						<Space direction="vertical" size="middle" style={{ width: '100%' }}>
							<div>
								<Switch
									checked={notifications.storageWarning}
									onChange={(checked) => this.handleSettingChange('notifications', 'storageWarning', checked)}
									checkedChildren="On"
									unCheckedChildren="Off"
								/>
								<Text style={{ marginLeft: 8 }}>
									Storage space warnings
								</Text>
							</div>
							
							<div>
								<Switch
									checked={notifications.errorAlerts}
									onChange={(checked) => this.handleSettingChange('notifications', 'errorAlerts', checked)}
									checkedChildren="On"
									unCheckedChildren="Off"
								/>
								<Text style={{ marginLeft: 8 }}>
									Error alerts
								</Text>
							</div>
						</Space>
					</Col>
					
					<Col xs={24}>
						<Divider />
						<Title level={5}>Delivery Methods</Title>
						<Space direction="vertical" size="middle" style={{ width: '100%' }}>
							<div>
								<Switch
									checked={notifications.pushNotifications}
									onChange={(checked) => this.handleSettingChange('notifications', 'pushNotifications', checked)}
									checkedChildren="On"
									unCheckedChildren="Off"
								/>
								<Text style={{ marginLeft: 8 }}>
									Push notifications
								</Text>
								<br />
								<Text type="secondary" style={{ marginLeft: 32, fontSize: '12px' }}>
									Show desktop notifications
								</Text>
							</div>
							
							<div>
								<Switch
									checked={notifications.emailNotifications}
									onChange={(checked) => this.handleSettingChange('notifications', 'emailNotifications', checked)}
									checkedChildren="On"
									unCheckedChildren="Off"
								/>
								<Text style={{ marginLeft: 8 }}>
									Email notifications
								</Text>
								<br />
								<Text type="secondary" style={{ marginLeft: 32, fontSize: '12px' }}>
									Send notifications to your email address
								</Text>
							</div>
						</Space>
					</Col>
				</Row>
			</div>
		);
	}

	renderAdvancedSettings = () => {
		return (
			<div className="settings-section">
				<Title level={4}>
					<SettingOutlined /> Advanced Settings
				</Title>
				
				<Alert
					message="Advanced Configuration"
					description="These settings are for advanced users only. Incorrect values may cause issues."
					type="error"
					showIcon
					style={{ marginBottom: 16 }}
				/>
				
				<Row gutter={[16, 16]}>
					<Col xs={24}>
						<Form.Item 
							label="Custom FFmpeg Parameters"
							help="Additional parameters for video encoding (experts only)"
						>
							<TextArea
								rows={3}
								placeholder="-preset ultrafast -crf 18"
							/>
						</Form.Item>
					</Col>
					
					<Col xs={24} md={12}>
						<Form.Item 
							label="Buffer Pool Size"
							help="Number of buffers to allocate"
						>
							<InputNumber
								min={4}
								max={32}
								defaultValue={8}
								style={{ width: '100%' }}
							/>
						</Form.Item>
					</Col>
					
					<Col xs={24} md={12}>
						<Form.Item 
							label="Thread Count"
							help="Number of processing threads (0 = auto)"
						>
							<InputNumber
								min={0}
								max={16}
								defaultValue={0}
								style={{ width: '100%' }}
							/>
						</Form.Item>
					</Col>
					
					<Col xs={24}>
						<Form.Item>
							<Space direction="vertical" style={{ width: '100%' }}>
								<Switch
									defaultChecked={false}
									checkedChildren="Debug On"
									unCheckedChildren="Debug Off"
								/>
								<Text type="secondary">Enable debug logging</Text>
							</Space>
						</Form.Item>
					</Col>
					
					<Col xs={24}>
						<Form.Item>
							<Space direction="vertical" style={{ width: '100%' }}>
								<Switch
									defaultChecked={false}
									checkedChildren="Experimental On"
									unCheckedChildren="Experimental Off"
								/>
								<Text type="secondary">Enable experimental features</Text>
							</Space>
						</Form.Item>
					</Col>
				</Row>
			</div>
		);
	}

	render() {
		const { visible, onCancel } = this.props;
		const { 
			activeTab, 
			loading, 
			saving, 
			importing, 
			exporting, 
			exportProgress,
			hasChanges,
			showAdvanced 
		} = this.state;

		return (
			<Modal
				title={
					<div className="settings-modal-title">
						<SettingOutlined />
						<span>Replay Settings</span>
					</div>
				}
				visible={visible}
				onCancel={onCancel}
				width={1000}
				className="replay-settings-modal"
				footer={
					<div className="settings-footer">
						<div className="footer-left">
							<Space>
								<Upload
									accept=".json"
									beforeUpload={this.handleImportSettings}
									showUploadList={false}
								>
									<Button 
										icon={<ImportOutlined />} 
										loading={importing}
									>
										Import Settings
									</Button>
								</Upload>
								
								<Button 
									icon={<ExportOutlined />} 
									onClick={this.handleExportSettings}
									loading={exporting}
								>
									Export Settings
								</Button>
								
								<Popconfirm
									title="Reset all settings to default values?"
									description="This action cannot be undone."
									onConfirm={this.resetSettings}
									okText="Reset"
									cancelText="Cancel"
									okType="danger"
								>
									<Button 
										icon={<ReloadOutlined />} 
										danger
									>
										Reset All
									</Button>
								</Popconfirm>
							</Space>
						</div>
						
						<div className="footer-right">
							<Space>
								<Button onClick={onCancel}>
									Cancel
								</Button>
								<Button 
									type="primary" 
									icon={<SaveOutlined />}
									onClick={this.saveSettings}
									loading={saving}
									disabled={!hasChanges}
								>
									Save Settings
								</Button>
							</Space>
						</div>
					</div>
				}
				destroyOnClose
				maskClosable={false}
			>
				{exporting && (
					<div className="export-progress">
						<Progress 
							percent={exportProgress} 
							status="active"
							format={() => 'Exporting...'}
						/>
					</div>
				)}
				
				<div className="settings-content">
					<Tabs
						activeKey={activeTab}
						onChange={(key) => this.setState({ activeTab: key })}
						type="card"
						className="settings-tabs"
					>
						<TabPane
							tab={
								<span>
									<VideoCameraOutlined />
									Recording
								</span>
							}
							key="recording"
						>
							{this.renderRecordingSettings()}
						</TabPane>
						
						<TabPane
							tab={
								<span>
									<DatabaseOutlined />
									Storage
								</span>
							}
							key="storage"
						>
							{this.renderStorageSettings()}
						</TabPane>
						
						<TabPane
							tab={
								<span>
									<PlayCircleOutlined />
									Playback
								</span>
							}
							key="playback"
						>
							{this.renderPlaybackSettings()}
						</TabPane>
						
						<TabPane
							tab={
								<span>
									<SecurityScanOutlined />
									Privacy
								</span>
							}
							key="privacy"
						>
							{this.renderPrivacySettings()}
						</TabPane>
						
						<TabPane
							tab={
								<span>
									<BugOutlined />
									Performance
								</span>
							}
							key="performance"
						>
							{this.renderPerformanceSettings()}
						</TabPane>
						
						<TabPane
							tab={
								<span>
									<GlobalOutlined />
									Notifications
								</span>
							}
							key="notifications"
						>
							{this.renderNotificationSettings()}
						</TabPane>
						
						<TabPane
							tab={
								<span>
									<SettingOutlined />
									Advanced
									<Tooltip title="For advanced users only">
										<QuestionCircleOutlined style={{ marginLeft: 4 }} />
									</Tooltip>
								</span>
							}
							key="advanced"
						>
							{this.renderAdvancedSettings()}
						</TabPane>
					</Tabs>
				</div>
				
				{hasChanges && (
					<Alert
						message="Unsaved Changes"
						description="You have unsaved changes. Don't forget to save your settings."
						type="warning"
						showIcon
						style={{ marginTop: 16 }}
						action={
							<Button 
								size="small" 
								type="primary" 
								onClick={this.saveSettings}
								loading={saving}
							>
								Save Now
							</Button>
						}
					/>
				)}
			</Modal>
		);
	}
}

export default ReplaySettings;
